/**
 * Classical item analysis for a test scored right/wrong.
 *
 * - **Difficulty p** — the share of students answering an item correctly (an omitted
 *   answer counts as wrong).
 * - **Discrimination D** — p in the upper group minus p in the lower group, the groups
 *   being the top and bottom 27% of students by total score (Kelley, 1939). Group size
 *   is the share × N rounded to the nearest whole student (at least one, at most half
 *   the class); students tied at a cut-off are split by row order.
 * - **Corrected point-biserial** — Pearson's r between the item (0/1) and the total of
 *   the remaining items, so the item is not correlated with itself.
 * - **KR-20** (Kuder & Richardson, 1937) — k / (k − 1) × (1 − Σpq / σ²), with σ² the
 *   variance of total scores divided by N to match the pq item variances. That makes it
 *   identical to Cronbach's alpha on the 0/1 matrix.
 *
 * Distractors follow Haladyna & Downing (1993): a wrong option chosen by fewer than 5% of
 * students is not doing its job, and one chosen more often by the upper group than the
 * lower group is drawing strong students away from the key.
 */

import { parseNumberInput } from "@/lib/parse-number";
import { mean, pearson, sum } from "@/lib/stats/descriptive";
import { isMissingToken } from "@/lib/tool-kit/research-design/matrix";

/** The thresholds behind the flags, compared at two decimals as displayed. */
export const ITEM_LIMITS = {
  /** p above this: almost everyone gets it right. */
  veryEasy: 0.9,
  /** p below this: almost nobody does. */
  veryHard: 0.2,
  /** D below this is "poor" in Ebel's bands. */
  weak: 0.2,
} as const;

/** A distractor chosen by less than this share of students is non-functioning. */
export const RARE_DISTRACTOR = 0.05;

const round2 = (value: number) => Math.round(value * 100) / 100;

/* ----------------------------------------------------------------- input */

/** A cell a 0/1 matrix can hold. */
export function isScoreCell(cell: string): boolean {
  if (isMissingToken(cell)) {
    return true;
  }
  const value = parseNumberInput(cell);
  return value === 0 || value === 1;
}

export interface ScoreMatrix {
  scores: number[][];
  /** Blank cells, scored 0. */
  blank: number;
  /** Cells other than 0, 1 or blank, scored 0. */
  invalid: number;
}

export function readScores(
  body: readonly (readonly string[])[],
  itemCount: number
): ScoreMatrix {
  let blank = 0;
  let invalid = 0;
  const scores = body.map((row) =>
    Array.from({ length: itemCount }, (_, item) => {
      const cell = row[item] ?? "";
      if (isMissingToken(cell)) {
        blank += 1;
        return 0;
      }
      const value = parseNumberInput(cell);
      if (value === 0 || value === 1) {
        return value;
      }
      invalid += 1;
      return 0;
    })
  );
  return { scores, blank, invalid };
}

const LETTER = /^[a-z]$/i;
const LETTER_RUN = /^[a-z]+$/i;
const KEY_SEPARATORS = /[\s,;]+/;

/** "B D A C", "B,D,A,C" and "BDAC" all read as B, D, A, C. */
export function parseAnswerKey(text: string): string[] {
  const key: string[] = [];
  for (const token of text.split(KEY_SEPARATORS)) {
    if (LETTER_RUN.test(token)) {
      key.push(...token.toUpperCase());
    }
  }
  return key;
}

/** A cell an answer sheet can hold: one letter, a blank, or a whole row typed as letters. */
export function isAnswerCell(cell: string, keyLength: number): boolean {
  const text = cell.trim();
  return (
    isMissingToken(text) ||
    LETTER.test(text) ||
    (keyLength > 1 && text.length === keyLength && LETTER_RUN.test(text))
  );
}

/** Rows typed as one run of letters ("ABDCA") become one answer per item. */
export function expandCompactRows(
  body: readonly (readonly string[])[],
  keyLength: number
): { body: string[][]; expanded: boolean } {
  let expanded = false;
  const out = body.map((row) => {
    const filled = row.map((cell) => cell.trim()).filter((cell) => cell !== "");
    const only = filled[0] ?? "";
    if (
      keyLength > 1 &&
      filled.length === 1 &&
      only.length === keyLength &&
      LETTER_RUN.test(only)
    ) {
      expanded = true;
      return [...only];
    }
    return [...row];
  });
  return { body: out, expanded };
}

export interface LetterScoring {
  scores: number[][];
  /** The answer each student gave, upper-cased; null for a blank. */
  responses: (string | null)[][];
  blank: number;
}

export function scoreLetters(
  body: readonly (readonly string[])[],
  key: readonly string[]
): LetterScoring {
  let blank = 0;
  const responses = body.map((row) =>
    key.map((_, item) => {
      const cell = (row[item] ?? "").trim().toUpperCase();
      if (isMissingToken(cell)) {
        blank += 1;
        return null;
      }
      return cell;
    })
  );
  const scores = responses.map((row) =>
    row.map((answer, item) => (answer !== null && answer === key[item] ? 1 : 0))
  );
  return { scores, responses, blank };
}

/* -------------------------------------------------------------- analysis */

export type ItemFlag = "very-easy" | "very-hard" | "weak" | "negative";

export interface ItemStatistics {
  p: number;
  /** p within the upper and lower groups. */
  upper: number;
  lower: number;
  d: number;
  /** Corrected point-biserial; null when the item or the rest score has no spread. */
  rRest: number | null;
  flags: ItemFlag[];
}

export interface TestAnalysis {
  students: number;
  items: ItemStatistics[];
  totals: number[];
  /** Row indices of the upper and lower groups. */
  upperGroup: number[];
  lowerGroup: number[];
  groupSize: number;
  mean: number;
  /** Variance of total scores, divided by N. */
  variance: number;
  sd: number;
  sumPq: number;
  kr20: number | null;
  /** Standard error of measurement: σ√(1 − KR-20). */
  sem: number | null;
}

/** Students in each extreme group: share × N, rounded, between 1 and N / 2. */
export function groupSizeFor(students: number, share: number): number {
  return Math.min(
    Math.floor(students / 2),
    Math.max(1, Math.round(share * students))
  );
}

function itemFlags(p: number, d: number, rRest: number | null): ItemFlag[] {
  const flags: ItemFlag[] = [];
  if (round2(p) > ITEM_LIMITS.veryEasy) {
    flags.push("very-easy");
  }
  if (round2(p) < ITEM_LIMITS.veryHard) {
    flags.push("very-hard");
  }
  if (round2(d) < 0 || (rRest !== null && round2(rRest) < 0)) {
    flags.push("negative");
  } else if (round2(d) < ITEM_LIMITS.weak) {
    flags.push("weak");
  }
  return flags;
}

export function analyzeTest(
  scores: readonly (readonly number[])[],
  share: number
): TestAnalysis | null {
  const students = scores.length;
  const itemCount = scores[0]?.length ?? 0;
  if (students < 2 || itemCount === 0) {
    return null;
  }
  const totals = scores.map((row) => sum(row));
  const order = totals
    .map((total, index) => ({ total, index }))
    .sort((a, b) => b.total - a.total || a.index - b.index)
    .map((entry) => entry.index);
  const groupSize = groupSizeFor(students, share);
  const upperGroup = order.slice(0, groupSize);
  const lowerGroup = order.slice(students - groupSize);
  const average = mean(totals);
  const variance =
    totals.reduce((acc, total) => acc + (total - average) ** 2, 0) / students;
  let sumPq = 0;
  const items = Array.from({ length: itemCount }, (_, item) => {
    const column = scores.map((row) => row[item]);
    const p = mean(column);
    sumPq += p * (1 - p);
    const upper = mean(upperGroup.map((index) => scores[index][item]));
    const lower = mean(lowerGroup.map((index) => scores[index][item]));
    const r = pearson(
      column,
      totals.map((total, index) => total - scores[index][item])
    );
    const rRest = Number.isNaN(r) ? null : r;
    const d = upper - lower;
    return { p, upper, lower, d, rRest, flags: itemFlags(p, d, rRest) };
  });
  const kr20 =
    itemCount > 1 && variance > 0
      ? (itemCount / (itemCount - 1)) * (1 - sumPq / variance)
      : null;
  const sd = Math.sqrt(variance);
  return {
    students,
    items,
    totals,
    upperGroup,
    lowerGroup,
    groupSize,
    mean: average,
    variance,
    sd,
    sumPq,
    kr20,
    sem: kr20 === null ? null : sd * Math.sqrt(Math.max(0, 1 - kr20)),
  };
}

/* ------------------------------------------------------------ distractors */

export type DistractorFlag = "never-chosen" | "rarely-chosen" | "draws-upper";

/** The options on the paper: A up to the furthest letter anyone used, or the key did. */
export function answerOptions(
  responses: readonly (readonly (string | null)[])[],
  key: readonly string[]
): string[] {
  let last = "A".charCodeAt(0);
  const consider = (answer: string | null) => {
    if (answer !== null && LETTER.test(answer)) {
      last = Math.max(last, answer.toUpperCase().charCodeAt(0));
    }
  };
  for (const row of responses) {
    for (const answer of row) {
      consider(answer);
    }
  }
  for (const answer of key) {
    consider(answer);
  }
  return Array.from({ length: last - 64 }, (_, i) =>
    String.fromCharCode(65 + i)
  );
}

/** Buckets for a blank and for an answer that is not one of the options ("AB"). */
export const OMITTED = "omitted";
export const OTHER = "other";

export interface OptionTally {
  /** A letter, `OMITTED` or `OTHER`. */
  option: string;
  all: number;
  upper: number;
  lower: number;
  isKey: boolean;
  flags: DistractorFlag[];
}

function distractorFlags(
  tally: OptionTally,
  students: number
): DistractorFlag[] {
  if (tally.isKey || tally.option === OMITTED || tally.option === OTHER) {
    return [];
  }
  const flags: DistractorFlag[] = [];
  if (tally.all === 0) {
    flags.push("never-chosen");
  } else if (tally.all / students < RARE_DISTRACTOR) {
    flags.push("rarely-chosen");
  }
  if (tally.upper > tally.lower) {
    flags.push("draws-upper");
  }
  return flags;
}

/** Per item: how many chose each option, overall and in each extreme group. */
export function tallyOptions(
  responses: readonly (readonly (string | null)[])[],
  key: readonly string[],
  analysis: TestAnalysis,
  options: readonly string[]
): OptionTally[][] {
  const upper = new Set(analysis.upperGroup);
  const lower = new Set(analysis.lowerGroup);
  return key.map((answerKey, item) => {
    const tallies = new Map<string, OptionTally>(
      [...options, OMITTED, OTHER].map((option) => [
        option,
        {
          option,
          all: 0,
          upper: 0,
          lower: 0,
          isKey: option === answerKey,
          flags: [],
        },
      ])
    );
    for (const [student, row] of responses.entries()) {
      const answer = row[item] ?? null;
      let bucket = OTHER;
      if (answer === null) {
        bucket = OMITTED;
      } else if (options.includes(answer)) {
        bucket = answer;
      }
      const tally = tallies.get(bucket) as OptionTally;
      tally.all += 1;
      tally.upper += upper.has(student) ? 1 : 0;
      tally.lower += lower.has(student) ? 1 : 0;
    }
    return [...tallies.values()]
      .filter(
        (tally) =>
          (tally.option !== OMITTED && tally.option !== OTHER) || tally.all > 0
      )
      .map((tally) => ({
        ...tally,
        flags: distractorFlags(tally, responses.length),
      }));
  });
}
